package app.nestbridge.feature.children.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import com.karawek.core.ui.components.CoreDateTextField
import com.karawek.core.ui.components.CoreDivider
import com.karawek.core.ui.components.CorePrimaryButton
import com.karawek.core.ui.components.CoreScaffold
import com.karawek.core.ui.components.CoreSecondaryButton
import com.karawek.core.ui.components.CoreTextField
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import app.nestbridge.feature.children.screen.CreateChildProfileEvent
import app.nestbridge.feature.children.screen.CreateChildProfileScreen
import app.nestbridge.feature.children.screen.CreateChildProfileState
import app.nestbridge.feature.children.screen.canContinue
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = CreateChildProfileScreen::class,
    scope = AppScope::class,
)
@Composable
fun CreateChildProfileUi(
    state: CreateChildProfileState,
    modifier: Modifier = Modifier,
) {
    CoreScaffold(
        title = "Add your child's information",
        subtitle = "This helps us personalize care coordination and track progress.",
        footer = { Footer(state) },
        onBack = if (state.onBoardingMode) null else { { state.eventSink(CreateChildProfileEvent.Back) } },
        modifier = modifier,
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(CoreSpacing.md),
        ) {
            CoreTextField(
                value = state.fullName,
                onValueChange = { state.eventSink(CreateChildProfileEvent.UpdateFullName(it)) },
                label = "Child's full name",
                placeholder = "e.g., Emma Johnson",
                modifier = Modifier.fillMaxWidth(),
                error = null,
            )

            Column(verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs)) {
                CoreTextField(
                    value = state.displayName,
                    onValueChange = { state.eventSink(CreateChildProfileEvent.UpdateDisplayName(it)) },
                    label = "Display name",
                    placeholder = "e.g., Emma",
                    modifier = Modifier.fillMaxWidth(),
                    error = null,
                )
                Text(
                    text = "How the child's name will appear in the app.",
                    style = CoreTypography.caption,
                    color = CoreTheme.colors.textSecondary,
                )
            }

            Column(verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs)) {
                CoreDateTextField(
                    value = state.dateOfBirth,
                    onValueChange = { state.eventSink(CreateChildProfileEvent.UpdateDateOfBirth(it)) },
                    label = "Date of birth (optional)",
                    placeholder = "mm/dd/yyyy",
                    modifier = Modifier.fillMaxWidth(),
                    error = null,
                )
                Text(
                    text = "Helps us show age-appropriate milestones and activities.",
                    style = CoreTypography.caption,
                    color = CoreTheme.colors.textSecondary,
                )
            }
        }

        state.errorMessage?.let {
            Text(
                text = it,
                style = CoreTypography.caption,
                color = CoreTheme.colors.error,
            )
        }
    }
}

@Composable
private fun Footer(state: CreateChildProfileState) =
    Column(
        verticalArrangement = Arrangement.spacedBy(CoreSpacing.md),
    ) {
        CorePrimaryButton(
            text = if (state.isSaving) "Saving..." else "Continue",
            enabled = state.canContinue(),
            onClick = { state.eventSink(CreateChildProfileEvent.Continue) },
            modifier = Modifier
                .fillMaxWidth(),
        )

        if (state.onBoardingMode) {
            CoreDivider()

            Column(
                verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
            ) {
                Text(
                    text = "Have a QR code? Scan it to review an invitation.",
                    style = CoreTypography.footnote,
                    color = CoreTheme.colors.textSecondary,
                )

                CoreSecondaryButton(
                    text = "Scan QR code",
                    onClick = { state.eventSink(CreateChildProfileEvent.ScanQRCode) },
                    modifier = Modifier
                        .fillMaxWidth(),
                )
            }
        }
    }

@Preview
@Composable
private fun CreateChildProfileUiPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        CreateChildProfileUi(
            state = CreateChildProfileState(
                fullName = "",
                displayName = "",
                dateOfBirth = "",
                isSaving = false,
                errorMessage = null,
                eventSink = {},
            ),
        )
    }
}
